/**
 * Keycloak Authentication Service
 *
 * Manages Keycloak initialization, authentication, token management, and user info.
 * Uses Authorization Code flow with PKCE for browser-based authentication.
 */

import Keycloak from 'keycloak-js';

let keycloakInstance = null;
let isInitialized = false;

/**
 * Initialize Keycloak with the provided configuration.
 *
 * @param {Object} config - Keycloak configuration object
 * @param {string} config.url - Keycloak server URL
 * @param {string} config.realm - Realm name
 * @param {string} config.clientId - Client ID
 * @returns {Promise<boolean>} - True if authenticated, false otherwise
 */
async function initKeycloak(config) {
  if (isInitialized) {
    console.warn('Keycloak is already initialized');
    return keycloakInstance.authenticated;
  }

  try {
    keycloakInstance = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    });

    const authenticated = await keycloakInstance.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    });

    isInitialized = true;

    // Setup token refresh
    setupTokenRefresh();

    console.log(`Keycloak initialized. Authenticated: ${authenticated}`);
    return authenticated;
  } catch (error) {
    console.error('Failed to initialize Keycloak:', error);
    throw error;
  }
}

/**
 * Setup automatic token refresh.
 * Proactively refreshes token before expiration (70 seconds before expiration).
 * Also handles the case where token has already expired.
 */
function setupTokenRefresh() {
  if (!keycloakInstance) {
    return;
  }

  // Proactive token refresh - check every 30 seconds and refresh if token will expire within 70 seconds
  const refreshInterval = setInterval(() => {
    if (!keycloakInstance || !keycloakInstance.authenticated) {
      clearInterval(refreshInterval);
      return;
    }

    // Refresh token if it will expire within 70 seconds
    keycloakInstance
      .updateToken(70)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed proactively');
        }
      })
      .catch((error) => {
        console.error('Failed to refresh token:', error);
        console.error('Session expired - redirecting to login');
        clearInterval(refreshInterval);
        // Token refresh failed, user needs to re-authenticate
        keycloakInstance.logout();
      });
  }, 30000); // Check every 30 seconds

  // Fallback: Handle case where token has already expired
  keycloakInstance.onTokenExpired = () => {
    console.warn('Token expired unexpectedly, attempting refresh...');
    keycloakInstance
      .updateToken(5)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed after expiration');
        } else {
          console.error('Token refresh failed - logging out');
          keycloakInstance.logout();
        }
      })
      .catch((error) => {
        console.error('Failed to refresh expired token:', error);
        console.error('Session expired - redirecting to login');
        // Token refresh failed, user needs to re-authenticate
        keycloakInstance.logout();
      });
  };
}

/**
 * Login to Keycloak.
 * Redirects to Keycloak login page.
 *
 * @param {Object} options - Login options
 * @param {string} options.redirectUri - URI to redirect to after login
 */
function login(options = {}) {
  if (!keycloakInstance) {
    console.error('Keycloak is not initialized');
    return;
  }

  keycloakInstance.login(options);
}

/**
 * Logout from Keycloak.
 * Redirects to Keycloak logout page.
 *
 * @param {Object} options - Logout options
 * @param {string} options.redirectUri - URI to redirect to after logout
 */
function logout(options = {}) {
  if (!keycloakInstance) {
    console.error('Keycloak is not initialized');
    return;
  }

  keycloakInstance.logout(options);
}

/**
 * Register a new user in Keycloak.
 * Redirects to Keycloak registration page.
 *
 * @param {Object} options - Registration options
 * @param {string} options.redirectUri - URI to redirect to after registration
 */
function register(options = {}) {
  if (!keycloakInstance) {
    console.error('Keycloak is not initialized');
    return;
  }

  keycloakInstance.register(options);
}

/**
 * Get the current access token.
 *
 * @returns {string|null} - Access token or null if not authenticated
 */
function getToken() {
  return keycloakInstance?.token || null;
}

/**
 * Get the current refresh token.
 *
 * @returns {string|null} - Refresh token or null if not authenticated
 */
function getRefreshToken() {
  return keycloakInstance?.refreshToken || null;
}

/**
 * Check if the user is authenticated.
 *
 * @returns {boolean} - True if authenticated, false otherwise
 */
function isAuthenticated() {
  return keycloakInstance?.authenticated || false;
}

/**
 * Get user information from the token.
 *
 * @returns {Object|null} - User info object with sub, username, email, firstName, lastName, phoneNumber, address, and roles, or null if not authenticated
 */
function getUserInfo() {
  if (!keycloakInstance || !keycloakInstance.tokenParsed) {
    return null;
  }

  return {
    sub: keycloakInstance.tokenParsed.sub,
    username: keycloakInstance.tokenParsed.preferred_username,
    email: keycloakInstance.tokenParsed.email,
    firstName: keycloakInstance.tokenParsed.given_name,
    lastName: keycloakInstance.tokenParsed.family_name,
    phoneNumber: keycloakInstance.tokenParsed.phone_number,
    address: keycloakInstance.tokenParsed.address,
    roles: keycloakInstance.tokenParsed.realm_access?.roles || [],
  };
}

/**
 * Update the token if it's about to expire.
 *
 * @param {number} minValidity - Minimum validity time in seconds (default: 5)
 * @returns {Promise<boolean>} - True if token was refreshed, false otherwise
 */
async function updateToken(minValidity = 5) {
  if (!keycloakInstance) {
    return false;
  }

  try {
    return await keycloakInstance.updateToken(minValidity);
  } catch (error) {
    console.error('Failed to update token:', error);
    return false;
  }
}

/**
 * Check if the user has a specific role.
 *
 * @param {string} role - Role name to check
 * @returns {boolean} - True if user has the role, false otherwise
 */
function hasRole(role) {
  return keycloakInstance?.hasRealmRole(role) || false;
}

/**
 * Get the Keycloak instance.
 *
 * @returns {Keycloak|null} - Keycloak instance or null if not initialized
 */
function getInstance() {
  return keycloakInstance;
}

export default {
  initKeycloak,
  login,
  logout,
  register,
  getToken,
  getRefreshToken,
  isAuthenticated,
  getUserInfo,
  updateToken,
  hasRole,
  getInstance,
};
